import { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { useAuth } from '@/contexts/AuthContext';
import { AdminNavigation } from '@/components/AdminNavigation';
import { SystemPerformanceMonitor } from '@/components/SystemPerformanceMonitor';
import { 
  Settings, Database, Download, Upload, RefreshCw, Clock, 
  Package, Users, ShoppingCart, TrendingUp, FileText, 
  Shield, CheckCircle, Info, AlertTriangle,
  Server, HardDrive, Activity, BookOpen, Mail, Globe,
  Lock, Eye, Key, RotateCcw, Save, Search, HelpCircle,
  Zap, Star, ArrowRight, ExternalLink, Store, Receipt,
  Tag, Layers
} from 'lucide-react';
import { Link } from 'wouter';

interface SystemInfo {
  version: string;
  environment: string;
  uptime: number;
  dbSize: number;
  totalProducts: number;
  totalRetailProducts: number;
  totalOrders: number;
  totalCustomers: number;
  totalUsers: number;
  totalRestaurants: number;
  totalRebates: number;
  totalDiscountCodes: number;
  totalTiers: number;
  lastBackup?: string;
}

interface BackupFile {
  filename: string;
  size: number;
  created: string;
  tables: string[];
}

// Guide data structure
interface GuideSection {
  id: string;
  title: string;
  category: string;
  icon: any;
  description: string;
  content: string;
  tags: string[];
  quickReference?: {
    shortcuts?: string[];
    tips?: string[];
    links?: { label: string; path: string }[];
  };
}

const guideData: GuideSection[] = [
  {
    id: 'retail-products',
    title: 'Retail Products Management',
    category: 'Products',
    icon: Package,
    description: 'Manage Chinese grocery products, pricing, and inventory',
    content: `
      The retail products section allows you to manage your Chinese grocery inventory:
      
      • **Add Products**: Click "Add Product" to create new items with descriptions, pricing, and categories
      • **Edit Products**: Click any product to modify details, pricing, or availability
      • **Categories**: Organize products by produce, meat, seafood, groceries
      • **Pricing**: Set retail price and your cost for profit margin tracking
      • **Images**: Upload product images for better visual identification
      • **Active Status**: Enable/disable products without deleting them
      • **Display Order**: Control the order products appear in listings
    `,
    tags: ['products', 'inventory', 'pricing', 'categories', 'chinese', 'grocery'],
    quickReference: {
      shortcuts: ['Ctrl+N: Add new product', 'Ctrl+S: Save changes', 'Esc: Cancel editing'],
      tips: [
        'Use clear, descriptive names in both English and Chinese',
        'Set competitive pricing based on market research',
        'Keep active status updated to reflect current availability'
      ],
      links: [{ label: 'Manage Products', path: '/admin/products' }]
    }
  },
  {
    id: 'restaurant-orders',
    title: 'Restaurant Orders',
    category: 'Orders',
    icon: ShoppingCart,
    description: 'Process and manage wholesale restaurant orders',
    content: `
      The restaurant orders system handles B2B wholesale orders:
      
      • **Order Processing**: View, confirm, and track restaurant orders
      • **Status Management**: Update orders through pending → confirmed → delivered
      • **Customer Details**: Access restaurant contact information and order history
      • **Delivery Scheduling**: Manage delivery dates and logistics
      • **Order Analytics**: Track order volume, revenue, and customer patterns
      • **Discount Codes**: Apply and manage volume discounts for regular customers
      • **Real-time Updates**: Live notifications for new orders and status changes
    `,
    tags: ['orders', 'restaurant', 'wholesale', 'b2b', 'delivery', 'status', 'analytics'],
    quickReference: {
      shortcuts: ['F5: Refresh orders', 'Ctrl+F: Search orders', 'Ctrl+P: Print order'],
      tips: [
        'Confirm orders promptly to maintain customer satisfaction',
        'Use order analytics to identify popular products',
        'Set up delivery routes efficiently to reduce costs'
      ],
      links: [{ label: 'View Orders', path: '/admin/restaurant-orders' }]
    }
  },
  {
    id: 'discount-system',
    title: 'Discount & Rebate System',
    category: 'Financial',
    icon: TrendingUp,
    description: 'Manage discount codes, rebates, and customer loyalty programs',
    content: `
      Comprehensive discount and rebate management:
      
      • **Discount Codes**: Generate 4-character codes for restaurants with tiered pricing
      • **Tier System**: tier0 (0%), tier1 (7%), tier2 (10%), tier3 (15%) discounts
      • **Product Tags**: Assign discount eligibility to specific products
      • **Usage Tracking**: Monitor discount code usage and effectiveness
      • **Customer Rebates**: Calculate annual rebates based on purchase volume
      • **Rebate Tiers**: Set up automatic rebate calculations
      • **Payment Tracking**: Track rebate payments and outstanding amounts
    `,
    tags: ['discounts', 'rebates', 'loyalty', 'tiers', 'codes', 'financial', 'customers'],
    quickReference: {
      shortcuts: ['Ctrl+D: Generate discount code', 'Ctrl+R: Calculate rebates'],
      tips: [
        'Regular customers should get tier2 or tier3 codes',
        'Monitor discount usage to prevent abuse',
        'Calculate rebates annually for tax purposes'
      ],
      links: [
        { label: 'Discount Codes', path: '/admin/restaurant-discounts' },
        { label: 'Customer Rebates', path: '/admin/rebates' }
      ]
    }
  },
  {
    id: 'customer-management',
    title: 'Customer Management',
    category: 'Customers',
    icon: Users,
    description: 'Manage customer relationships and purchase history',
    content: `
      Complete customer relationship management:
      
      • **Customer Profiles**: Store business information, contacts, and preferences
      • **Purchase History**: Track all customer orders and spending patterns
      • **Communication Tools**: Email integration for order updates and promotions
      • **Loyalty Tracking**: Monitor customer lifetime value and purchase frequency
      • **Rebate Calculations**: Automatic annual rebate calculations based on volume
      • **Account Management**: Handle customer accounts, credit terms, and special pricing
      • **Segmentation**: Group customers by purchase volume, location, or business type
    `,
    tags: ['customers', 'crm', 'communication', 'loyalty', 'history', 'accounts'],
    quickReference: {
      shortcuts: ['Ctrl+K: Quick customer search', 'Ctrl+E: Send email'],
      tips: [
        'Keep customer contact information updated',
        'Use purchase history to recommend products',
        'Segment customers for targeted marketing'
      ],
      links: [{ label: 'Customer List', path: '/admin/customers' }]
    }
  },
  {
    id: 'system-monitoring',
    title: 'System Performance',
    category: 'Technical',
    icon: Activity,
    description: 'Monitor system health, performance, and uptime',
    content: `
      Real-time system monitoring and performance tracking:
      
      • **Performance Metrics**: CPU, memory, disk, and network usage
      • **Database Health**: Connection pool status, query performance, cache hit rates
      • **API Monitoring**: Request rates, response times, error tracking
      • **Uptime Tracking**: System availability and downtime alerts
      • **Resource Alerts**: Automated warnings for high resource usage
      • **Historical Data**: Performance trends and capacity planning
      • **Health Checks**: Automated system health verification
    `,
    tags: ['performance', 'monitoring', 'system', 'health', 'metrics', 'uptime', 'alerts'],
    quickReference: {
      shortcuts: ['F5: Refresh metrics', 'Ctrl+M: View alerts'],
      tips: [
        'Monitor during peak hours for performance issues',
        'Set up alerts for critical thresholds',
        'Use historical data for capacity planning'
      ],
      links: [{ label: 'Performance Dashboard', path: '/admin/settings?tab=performance' }]
    }
  },
  {
    id: 'security-backup',
    title: 'Security & Backup',
    category: 'Security',
    icon: Shield,
    description: 'Data protection, backups, and security management',
    content: `
      Comprehensive security and data protection:
      
      • **Database Backups**: Create and restore full system backups
      • **User Management**: Admin and read-only user account management
      • **Session Security**: Session management and access control
      • **Audit Logging**: Track system access and changes
      • **Data Export**: Export data for analysis or migration
      • **Password Policies**: Secure password requirements
      • **Access Control**: Role-based permissions and restrictions
    `,
    tags: ['security', 'backup', 'users', 'audit', 'data', 'protection', 'access'],
    quickReference: {
      shortcuts: ['Ctrl+B: Create backup', 'Ctrl+U: User management'],
      tips: [
        'Create backups before major system changes',
        'Use strong passwords for admin accounts',
        'Regular audit log reviews for security'
      ],
      links: [
        { label: 'Security Settings', path: '/admin/settings?tab=security' },
        { label: 'Database Backup', path: '/admin/settings?tab=database' }
      ]
    }
  }
];

export default function AdminSettings() {
  const [isBackupModalOpen, setIsBackupModalOpen] = useState(false);
  const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [backupDescription, setBackupDescription] = useState('');
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const { user, isAdmin } = useAuth();
  const { toast } = useToast();

  // Filter and search guide data
  const filteredGuideData = useMemo(() => {
    return guideData.filter(section => {
      const matchesCategory = selectedCategory === 'All' || section.category === selectedCategory;
      const matchesSearch = searchQuery === '' || 
        section.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        section.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        section.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
        section.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
      
      return matchesCategory && matchesSearch;
    });
  }, [searchQuery, selectedCategory]);

  const categories = ['All', ...Array.from(new Set(guideData.map(section => section.category)))];

  // System Info Query
  const { data: systemInfo, isLoading: systemInfoLoading } = useQuery<SystemInfo>({
    queryKey: ['admin-system-info'],
    queryFn: () => apiRequest('/api/admin/system/info'),
    enabled: isAdmin()
  });

  // Available Backups Query
  const { data: backups, isLoading: backupsLoading } = useQuery<BackupFile[]>({
    queryKey: ['admin-backups'],
    queryFn: () => apiRequest('/api/admin/system/backups'),
    enabled: isAdmin()
  });

  // Create Backup Mutation
  const createBackupMutation = useMutation({
    mutationFn: (description: string) => 
      apiRequest('POST', '/api/admin/system/backup', { description }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-backups'] });
      queryClient.invalidateQueries({ queryKey: ['admin-system-info'] });
      toast({ title: 'Backup created successfully' });
      
      // Download the backup file
      const link = document.createElement('a');
      link.href = `/api/admin/system/backup/${data.filename}/download`;
      link.download = data.filename;
      link.click();
      
      setIsBackupModalOpen(false);
      setBackupDescription('');
    },
    onError: (error: any) => {
      toast({ 
        title: 'Failed to create backup', 
        description: error.message,
        variant: 'destructive' 
      });
    }
  });

  // Restore Backup Mutation
  const restoreBackupMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('backup', file);
      
      // Get CSRF token and session ID
      const csrfToken = localStorage.getItem('csrfToken');
      const sessionId = localStorage.getItem('sessionId');
      const token = localStorage.getItem('token');
      
      const headers: HeadersInit = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      }
      if (sessionId) {
        headers['X-Session-Id'] = sessionId;
      }
      
      const response = await fetch('/api/admin/system/restore', {
        method: 'POST',
        headers,
        body: formData
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to restore backup');
      }
      
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries();
      
      const details = data.details;
      toast({ 
        title: 'Database restored successfully',
        description: details ? 
          `Restored ${details.totalRecords} records from ${details.restoredTables.length} tables` :
          'The application will reload shortly.'
      });
      
      // Reload the page after a short delay
      setTimeout(() => {
        window.location.reload();
      }, 2000);
      
      setIsRestoreModalOpen(false);
      setSelectedFile(null);
    },
    onError: (error: any) => {
      toast({ 
        title: 'Failed to restore backup', 
        description: error.message || 'Unknown error',
        variant: 'destructive' 
      });
    }
  });


  const handleBackupCreate = () => {
    createBackupMutation.mutate(backupDescription);
  };

  const handleFileRestore = () => {
    if (!selectedFile) {
      toast({ title: 'Please select a backup file', variant: 'destructive' });
      return;
    }
    
    if (confirm('This will completely replace all current data. Are you sure?')) {
      restoreBackupMutation.mutate(selectedFile);
    }
  };


  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatUptime = (seconds: number): string => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${days}d ${hours}h ${minutes}m`;
  };

  if (!isAdmin()) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <Shield className="w-16 h-16 mx-auto text-red-400 mb-4" />
            <h3 className="text-lg font-semibold mb-2">Access Denied</h3>
            <p className="text-gray-600">You need admin privileges to access settings.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavigation />

      <div className="container mx-auto p-4 max-w-6xl space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">System Settings</h1>
            <p className="text-gray-600 mt-1">Manage system configuration and data</p>
          </div>
          <Badge className="bg-green-100 text-green-800">
            <Activity className="w-3 h-3 mr-1" />
            System Online
          </Badge>
        </div>

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="performance">
              <Activity className="w-4 h-4 mr-1" />
              Performance
            </TabsTrigger>
            <TabsTrigger value="database">Database</TabsTrigger>
            <TabsTrigger value="security">Security</TabsTrigger>
            <TabsTrigger value="guide">
              <BookOpen className="w-4 h-4 mr-1" />
              Guide
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* System Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Server className="w-5 h-5" />
                  System Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                {systemInfoLoading ? (
                  <div className="animate-pulse space-y-4">
                    <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <Package className="w-8 h-8 mx-auto text-blue-600 mb-2" />
                      <p className="text-2xl font-bold">{systemInfo?.totalRetailProducts || 0}</p>
                      <p className="text-sm text-gray-600">Retail Products</p>
                    </div>
                    <div className="text-center p-4 bg-indigo-50 rounded-lg">
                      <Store className="w-8 h-8 mx-auto text-indigo-600 mb-2" />
                      <p className="text-2xl font-bold">{systemInfo?.totalProducts || 0}</p>
                      <p className="text-sm text-gray-600">Restaurant Products</p>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <ShoppingCart className="w-8 h-8 mx-auto text-green-600 mb-2" />
                      <p className="text-2xl font-bold">{systemInfo?.totalOrders || 0}</p>
                      <p className="text-sm text-gray-600">Orders</p>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                      <Users className="w-8 h-8 mx-auto text-purple-600 mb-2" />
                      <p className="text-2xl font-bold">{systemInfo?.totalCustomers || 0}</p>
                      <p className="text-sm text-gray-600">Customers</p>
                    </div>
                    <div className="text-center p-4 bg-amber-50 rounded-lg">
                      <Shield className="w-8 h-8 mx-auto text-amber-600 mb-2" />
                      <p className="text-2xl font-bold">{systemInfo?.totalUsers || 0}</p>
                      <p className="text-sm text-gray-600">Users</p>
                    </div>
                    <div className="text-center p-4 bg-emerald-50 rounded-lg">
                      <Receipt className="w-8 h-8 mx-auto text-emerald-600 mb-2" />
                      <p className="text-2xl font-bold">{systemInfo?.totalRebates || 0}</p>
                      <p className="text-sm text-gray-600">Rebates</p>
                    </div>
                    <div className="text-center p-4 bg-pink-50 rounded-lg">
                      <Tag className="w-8 h-8 mx-auto text-pink-600 mb-2" />
                      <p className="text-2xl font-bold">{systemInfo?.totalDiscountCodes || 0}</p>
                      <p className="text-sm text-gray-600">Discount Codes</p>
                    </div>
                    <div className="text-center p-4 bg-cyan-50 rounded-lg">
                      <Layers className="w-8 h-8 mx-auto text-cyan-600 mb-2" />
                      <p className="text-2xl font-bold">{systemInfo?.totalTiers || 0}</p>
                      <p className="text-sm text-gray-600">Tiers</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Button 
                    onClick={() => setIsBackupModalOpen(true)}
                    className="h-20 flex flex-col items-center justify-center"
                  >
                    <Download className="w-6 h-6 mb-2" />
                    Create Backup
                  </Button>
                  <Button 
                    onClick={() => setIsRestoreModalOpen(true)}
                    variant="outline"
                    className="h-20 flex flex-col items-center justify-center"
                  >
                    <Upload className="w-6 h-6 mb-2" />
                    Restore Data
                  </Button>
                  <Button 
                    onClick={() => window.location.reload()}
                    variant="outline"
                    className="h-20 flex flex-col items-center justify-center"
                  >
                    <RefreshCw className="w-6 h-6 mb-2" />
                    Restart System
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Performance Tab */}
          <TabsContent value="performance" className="space-y-6">
            <SystemPerformanceMonitor />
          </TabsContent>

          {/* Database Tab */}
          <TabsContent value="database" className="space-y-6">
            {/* Database Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="w-5 h-5" />
                  Database Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Database Size</span>
                    <span className="font-medium">{formatFileSize(systemInfo?.dbSize || 0)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Last Backup</span>
                    <span className="font-medium">
                      {systemInfo?.lastBackup ? new Date(systemInfo.lastBackup).toLocaleString() : 'Never'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">System Uptime</span>
                    <span className="font-medium">{formatUptime(systemInfo?.uptime || 0)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Backup Management */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <HardDrive className="w-5 h-5" />
                    Backup Management
                  </span>
                  <Button onClick={() => setIsBackupModalOpen(true)}>
                    <Download className="w-4 h-4 mr-2" />
                    Create Backup
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {backupsLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
                    <p className="mt-2 text-gray-600">Loading backups...</p>
                  </div>
                ) : backups && backups.length > 0 ? (
                  <div className="space-y-3">
                    {backups.map((backup, index) => (
                      <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <p className="font-medium">{backup.filename}</p>
                          <p className="text-sm text-gray-600">
                            {formatFileSize(backup.size)} • {new Date(backup.created).toLocaleString()}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              const link = document.createElement('a');
                              link.href = `/api/admin/system/backup/${backup.filename}/download`;
                              link.download = backup.filename;
                              link.click();
                            }}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              // TODO: Implement restore from existing backup
                              toast({ 
                                title: 'Not implemented yet',
                                description: 'Use the restore dialog to upload a backup file',
                                variant: 'destructive'
                              });
                            }}
                          >
                            <Upload className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Database className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <p>No backups found</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Image Backup Management */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Package className="w-5 h-5" />
                    Image Backup
                  </span>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline"
                      onClick={async () => {
                        try {
                          const response = await fetch('/api/admin/system/export-images', {
                            headers: {
                              'Authorization': `Bearer ${localStorage.getItem('token')}`
                            }
                          });
                          
                          if (!response.ok) {
                            throw new Error('Failed to export images');
                          }
                          
                          const blob = await response.blob();
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `product-images-${new Date().toISOString().split('T')[0]}.zip`;
                          a.click();
                          window.URL.revokeObjectURL(url);
                          
                          toast({ 
                            title: 'Images exported successfully',
                            description: 'ZIP file downloaded to your computer'
                          });
                        } catch (error: any) {
                          toast({ 
                            title: 'Export failed', 
                            description: error.message,
                            variant: 'destructive' 
                          });
                        }
                      }}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export Images
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = '.zip';
                        input.onchange = async (e) => {
                          const file = (e.target as HTMLInputElement).files?.[0];
                          if (!file) return;
                          
                          try {
                            const formData = new FormData();
                            formData.append('images', file);
                            
                            const response = await fetch('/api/admin/system/import-images', {
                              method: 'POST',
                              headers: {
                                'Authorization': `Bearer ${localStorage.getItem('token')}`
                              },
                              body: formData
                            });
                            
                            const result = await response.json();
                            
                            if (!response.ok) {
                              throw new Error(result.message || 'Import failed');
                            }
                            
                            toast({ 
                              title: 'Images imported successfully',
                              description: `Imported: ${result.details.imported}, Skipped: ${result.details.skipped}`
                            });
                          } catch (error: any) {
                            toast({ 
                              title: 'Import failed', 
                              description: error.message,
                              variant: 'destructive' 
                            });
                          }
                        };
                        input.click();
                      }}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Import Images
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div className="text-sm">
                        <p className="font-medium text-blue-800">Image Backup Information</p>
                        <div className="text-blue-700 mt-2 space-y-1">
                          <p><strong>Export:</strong> Downloads all product images as a ZIP file for backup or transfer purposes.</p>
                          <p><strong>Import:</strong> Uploads and extracts images from a ZIP file to restore them to the system.</p>
                          <p><strong>Note:</strong> Regular database backups only store image URLs/paths, not the actual image files.</p>
                          <p><strong>Location:</strong> Images are stored in <code className="bg-blue-100 px-1 rounded">/public/images/products/</code></p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                      <div className="text-sm">
                        <p className="font-medium text-amber-800">Usage Instructions</p>
                        <div className="text-amber-700 mt-2 space-y-1">
                          <p><strong>Export Process:</strong></p>
                          <ul className="list-disc list-inside ml-4 space-y-1">
                            <li>Click "Export Images" to download all product images as a ZIP file</li>
                            <li>File includes all images (JPEG, PNG, WebP) from the products directory</li>
                            <li>Use for backups, moving to another server, or disaster recovery</li>
                          </ul>
                          <p className="mt-3"><strong>Import Process:</strong></p>
                          <ul className="list-disc list-inside ml-4 space-y-1">
                            <li>Click "Import Images" and select a ZIP file containing product images</li>
                            <li>Images will be extracted to the products directory</li>
                            <li>Existing images with same names will be overwritten</li>
                            <li>Only image files (JPEG, PNG, WebP, GIF) will be imported</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lock className="w-5 h-5" />
                  Security Settings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div>
                    <h4 className="font-medium mb-3">Session Information</h4>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Current User</span>
                        <span className="font-medium">{user?.username}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Role</span>
                        <Badge className={user?.role === 'admin' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}>
                          {user?.role}
                        </Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Session Active</span>
                        <Badge className="bg-green-100 text-green-800">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Valid
                        </Badge>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-medium mb-3">Security Actions</h4>
                    <div className="space-y-3">
                      <Button variant="outline" className="w-full justify-start">
                        <Key className="w-4 h-4 mr-2" />
                        Change Password
                      </Button>
                      <Button variant="outline" className="w-full justify-start">
                        <Eye className="w-4 h-4 mr-2" />
                        View Audit Log
                      </Button>
                      <Button variant="outline" className="w-full justify-start">
                        <RotateCcw className="w-4 h-4 mr-2" />
                        Invalidate All Sessions
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Guide Tab */}
          <TabsContent value="guide" className="space-y-6">
            {/* Search and Filter */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5" />
                  System Guide & Quick Reference
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Search Bar */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      placeholder="Search features, shortcuts, or help topics..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>

                  {/* Category Filter */}
                  <div className="flex flex-wrap gap-2">
                    {categories.map(category => (
                      <Button
                        key={category}
                        variant={selectedCategory === category ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSelectedCategory(category)}
                      >
                        {category}
                      </Button>
                    ))}
                  </div>

                  {/* Search Results Summary */}
                  {(searchQuery || selectedCategory !== 'All') && (
                    <div className="text-sm text-gray-600">
                      {filteredGuideData.length} result{filteredGuideData.length !== 1 ? 's' : ''} found
                      {searchQuery && ` for "${searchQuery}"`}
                      {selectedCategory !== 'All' && ` in ${selectedCategory}`}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Guide Sections */}
            <div className="space-y-4">
              {filteredGuideData.length === 0 ? (
                <Card>
                  <CardContent className="text-center py-12">
                    <HelpCircle className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <h3 className="text-lg font-medium text-gray-700 mb-2">No results found</h3>
                    <p className="text-gray-500">
                      Try adjusting your search terms or selecting a different category.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                filteredGuideData.map(section => {
                  const IconComponent = section.icon;
                  return (
                    <Card key={section.id} className="overflow-hidden">
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-100 rounded-lg">
                              <IconComponent className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                              <CardTitle className="text-lg">{section.title}</CardTitle>
                              <p className="text-sm text-gray-600 mt-1">{section.description}</p>
                            </div>
                          </div>
                          <Badge variant="outline" className="text-xs">
                            {section.category}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* Main Content */}
                        <div className="prose prose-sm max-w-none">
                          {section.content.split('\n').map((line, index) => {
                            if (line.trim() === '') return <br key={index} />;
                            if (line.trim().startsWith('•')) {
                              const [bullet, ...rest] = line.split('•')[1].trim().split('**');
                              const boldPart = rest[0];
                              const normalPart = rest.slice(1).join('**').replace(/^\*\*/, '').replace(/\*\*$/, '');
                              return (
                                <div key={index} className="flex items-start gap-2 mb-2">
                                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                                  <p className="text-sm">
                                    <strong>{boldPart}</strong>{normalPart}
                                  </p>
                                </div>
                              );
                            }
                            return <p key={index} className="text-sm text-gray-700">{line.trim()}</p>;
                          })}
                        </div>

                        {/* Quick Reference */}
                        {section.quickReference && (
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-gray-200">
                            {/* Shortcuts */}
                            {section.quickReference.shortcuts && (
                              <div>
                                <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                                  <Zap className="w-3 h-3" />
                                  Shortcuts
                                </h4>
                                <div className="space-y-1">
                                  {section.quickReference.shortcuts.map((shortcut, index) => (
                                    <code key={index} className="text-xs bg-gray-100 px-2 py-1 rounded block">
                                      {shortcut}
                                    </code>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Tips */}
                            {section.quickReference.tips && (
                              <div>
                                <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                                  <Star className="w-3 h-3" />
                                  Tips
                                </h4>
                                <div className="space-y-1">
                                  {section.quickReference.tips.map((tip, index) => (
                                    <p key={index} className="text-xs text-gray-600">
                                      {tip}
                                    </p>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Quick Links */}
                            {section.quickReference.links && (
                              <div>
                                <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                                  <ArrowRight className="w-3 h-3" />
                                  Quick Links
                                </h4>
                                <div className="space-y-1">
                                  {section.quickReference.links.map((link, index) => (
                                    <Link key={index} href={link.path}>
                                      <Button 
                                        variant="ghost" 
                                        size="sm" 
                                        className="h-auto p-1 text-xs text-blue-600 hover:text-blue-700"
                                      >
                                        {link.label}
                                        <ExternalLink className="w-3 h-3 ml-1" />
                                      </Button>
                                    </Link>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Tags */}
                        <div className="flex flex-wrap gap-1 pt-2">
                          {section.tags.map(tag => (
                            <Badge 
                              key={tag} 
                              variant="secondary" 
                              className="text-xs cursor-pointer hover:bg-gray-200"
                              onClick={() => setSearchQuery(tag)}
                            >
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Backup Modal */}
      <Dialog open={isBackupModalOpen} onOpenChange={setIsBackupModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Database Backup</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="description">Backup Description</Label>
              <Input
                id="description"
                value={backupDescription}
                onChange={(e) => setBackupDescription(e.target.value)}
                placeholder="e.g., Before major update, End of month backup..."
              />
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-blue-800">Backup Contents</p>
                  <p className="text-blue-700 mt-1">
                    This backup will include all retail products, restaurant products, orders, customers, restaurants, rebates, discount codes, tiers, users, and system settings.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsBackupModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleBackupCreate} disabled={createBackupMutation.isPending}>
              {createBackupMutation.isPending ? 'Creating...' : 'Create Backup'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restore Backup Modal */}
      <Dialog open={isRestoreModalOpen} onOpenChange={setIsRestoreModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Restore Database</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="backup-file">Select Backup File</Label>
              <Input
                id="backup-file"
                type="file"
                accept=".json,.sql,.db"
                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
              />
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-red-800">Warning</p>
                  <p className="text-red-700 mt-1">
                    This will completely replace all current data with the backup. This action cannot be undone.
                  </p>
                </div>
              </div>
            </div>
            
            {selectedFile && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-blue-800">Selected File</p>
                    <p className="text-blue-700 mt-1">
                      {selectedFile.name} ({formatFileSize(selectedFile.size)})
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {restoreBackupMutation.isPending && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900" />
                  <div className="text-sm">
                    <p className="font-medium text-gray-800">Restoring database...</p>
                    <p className="text-gray-600 mt-1">
                      Please wait while your data is being restored. Do not close this window.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRestoreModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleFileRestore} 
              disabled={!selectedFile || restoreBackupMutation.isPending}
            >
              {restoreBackupMutation.isPending ? 'Restoring...' : 'Restore Database'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}